import { useState } from 'react';
import {
  Box, Card, CardContent, TextField, Button, Typography, Alert,
  Grid, InputAdornment, IconButton, CircularProgress, Link, Divider,
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import axios from 'axios';
import { useNavigate, useLocation } from 'react-router-dom';
import { fetchAndStorePermissions, landingPath } from '../utils/permissions';

const API = process.env.REACT_APP_API_URL;

const Login = () => {
  const location = useLocation();
  const [identifier, setIdentifier] = useState('');
  const [password,   setPassword]   = useState('');
  const [showPwd,    setShowPwd]    = useState(false);
  const [error,      setError]      = useState('');
  const [loading,    setLoading]    = useState(false);
  const navigate = useNavigate();

  // Message passed from ForgotPassword on successful reset
  const infoMsg = location.state?.info || '';

  const handleLogin = async () => {
    if (!identifier || !password) { setError('Email/username and password are required'); return; }
    setLoading(true); setError('');
    try {
      const res = await axios.post(`${API}/api/auth/login`, { email: identifier, password });
      const { token, user, school } = res.data;
      localStorage.setItem('token', token);
      localStorage.setItem('user', JSON.stringify(user));
      if (school) localStorage.setItem('school', JSON.stringify(school));
      else localStorage.removeItem('school');
      localStorage.removeItem('permissions');
      // Staff users are gated per-module by PermissionSets/UserGroups; admins,
      // super-admins and parents don't need this (they have their own access
      // rules). Fetch first so landingPath() can route to the right role centre.
      if (!user.isSuperAdmin && user.role !== 'parent' && user.role !== 'admin') {
        await fetchAndStorePermissions(token);
      }

      if (user.mustChangePassword)        navigate('/change-password');
      else if (user.isSuperAdmin)         navigate('/admin/schools');
      // Parents/guardians go straight to the parent portal
      else if (user.role === 'parent')    navigate('/parent');
      // No school yet — wait for an admin to assign one (or create your own)
      else if (!user.hasSchool)           navigate('/school-pending');
      else if (school?.provisionStatus !== 'Active') navigate('/school-pending');
      // Land on the user's own role centre (Finance, Academics, HR, …) based on
      // their permissions — falls back to the all-centres dashboard.
      else                                navigate(landingPath());
    } catch (err) {
      if (err.response?.data?.needsVerification) {
        navigate(`/verify-email?email=${encodeURIComponent(err.response.data.email)}`);
        return;
      }
      if (err.code === 'ERR_NETWORK') {
        setError('Cannot connect to server. Please ensure the backend is running.');
      } else {
        setError(err.response?.data?.error || 'Login failed. Please check your credentials.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#f4f6f8' }}>
      <Box sx={{ width: '100%', maxWidth: 400, px: 2 }}>
        <Card elevation={4} sx={{ borderRadius: 3 }}>
          <CardContent sx={{ p: 4 }}>
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
              <img src="/masomis.png" alt="Logo" style={{ height: 60, width: 'auto' }} />
            </Box>
            <Typography variant="h5" fontWeight={700} align="center" color="primary" mb={0.5}>EduMIS</Typography>
            <Typography variant="body2" color="text.secondary" align="center" mb={3}>
              Education Management Information System
            </Typography>

            {infoMsg && <Alert severity="success" sx={{ mb: 2 }}>{infoMsg}</Alert>}
            {error   && <Alert severity="error"   sx={{ mb: 2 }}>{error}</Alert>}

            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField label="Email or Username" value={identifier}
                  onChange={e => setIdentifier(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && handleLogin()}
                  fullWidth size="small" autoFocus autoComplete="username" />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Password" type={showPwd ? 'text' : 'password'}
                  value={password} onChange={e => setPassword(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && handleLogin()}
                  fullWidth size="small" autoComplete="current-password"
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton size="small" onClick={() => setShowPwd(v => !v)} edge="end">
                          {showPwd ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} display="flex" justifyContent="flex-end">
                <Link component="button" variant="body2" onClick={() => navigate('/forgot-password')}>
                  Forgot password?
                </Link>
              </Grid>
              <Grid item xs={12}>
                <Button variant="contained" fullWidth size="large"
                  onClick={handleLogin} disabled={loading}
                  startIcon={loading ? <CircularProgress size={18} color="inherit" /> : null}>
                  {loading ? 'Signing in…' : 'Sign In'}
                </Button>
              </Grid>
            </Grid>

            <Divider sx={{ my: 2.5 }} />

            <Typography variant="body2" color="text.secondary" align="center">
              Don't have an account?{' '}
              <Link component="button" fontWeight={700} onClick={() => navigate('/register')}>
                Create one
              </Link>
            </Typography>
          </CardContent>
        </Card>
        <Typography variant="caption" color="text.secondary" display="block" align="center" mt={2}>
          <Link href="https://www.dataportech.com" target="_blank" rel="noopener noreferrer"
            sx={{ color: 'text.secondary', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}>
            EduMIS by Dataport Technologies
          </Link>
          &nbsp;·&nbsp;
          <Link href="https://edumis.dataportech.com" target="_blank" rel="noopener noreferrer"
            sx={{ color: 'text.secondary', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}>
            edumis.dataportech.com
          </Link>
        </Typography>
      </Box>
    </Box>
  );
};

export default Login;
